import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Search, Briefcase, Send, Users, Archive, RotateCcw, X, MapPin, Phone, Mail } from 'lucide-react';
import { Button, Modal, StatusPill } from '../../../components';
import { homeAgencyService } from '../../../services/agencyService';
import {
  CARD, inputClass, FieldLabel, FilterTabs, EmptyCard, errorMessage, stampOf, CONNECTION_STATUS,
} from '../../agency/shared';

/**
 * Agencies > Connections. A home finds a staffing agency BY NAME (agencies
 * are created by ServPatch, never here) and sends it a request; the agency
 * accepts or declines from its own workspace. Connected agencies can be
 * archived — not while they have shifts to work or hours to approve — and
 * restored later on the same connection.
 */

const FindAgency = ({ open, onClose, onRequested }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setQ(''); setResults([]); setChosen(null); setMessage(''); } }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await homeAgencyService.search(q.trim());
        setResults(res.data || []);
      } catch (err) {
        toast.error(errorMessage(err));
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, open]);

  const send = async () => {
    setBusy(true);
    try {
      const res = await homeAgencyService.requestConnection(chosen.id, message);
      toast.success(res.message);
      onRequested();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const statusNote = (c) => {
    if (!c) return null;
    if (c.status === 'connected') return 'Already connected';
    if (c.status === 'pending') return 'Request waiting for the agency';
    if (c.status === 'archived') return 'Archived — restore it from History';
    return null;
  };

  return (
    <Modal
      isOpen={open}
      onClose={busy ? undefined : onClose}
      title={chosen ? `Request ${chosen.name}` : 'Find an agency'}
      maxWidth="max-w-lg"
      footer={chosen ? (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setChosen(null)} disabled={busy}>Back</Button>
          <Button leftIcon={<Send className="w-4 h-4" />} onClick={send} loading={busy}>Send request</Button>
        </div>
      ) : null}
    >
      {!chosen ? (
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
            <input autoFocus className={`${inputClass} pl-9`} placeholder="Agency name (or AOCH ID)" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {q.trim().length < 2 && <p className="text-[12.5px] text-[#94A3B8]">Type at least 2 letters of the agency's name.</p>}
          {searching && <p className="text-[12.5px] text-[#94A3B8]">Searching…</p>}
          {!searching && q.trim().length >= 2 && !results.length && (
            <p className="text-[13px] text-[#64748B]">No agency found. Agencies are set up by ServPatch — ask the agency to contact ServPatch if it isn't on the platform yet.</p>
          )}
          <ul className="flex flex-col gap-2">
            {results.map((a) => {
              const note = statusNote(a.connection);
              return (
                <li key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#E3E9F3] dark:border-gray-800 p-3">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{a.name}</p>
                    <p className="text-[12px] text-[#94A3B8] truncate">{[a.org_id, a.address].filter(Boolean).join(' · ')}</p>
                    {note && <p className="text-[12px] text-amber-700 mt-0.5">{note}</p>}
                    {['declined', 'cancelled'].includes(a.connection?.status) && (
                      <p className="text-[12px] text-[#64748B] mt-0.5">Previous request {a.connection.status}{a.connection.reason ? `: ${a.connection.reason}` : ''}</p>
                    )}
                  </div>
                  {!note && (
                    <Button size="sm" onClick={() => setChosen(a)}>
                      {a.connection ? 'Request again' : 'Request'}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-[13.5px] text-[#475569] dark:text-gray-300">
            {chosen.name} is asked to connect with your home. Until they accept, they see nothing of your home and you
            can't see their staff.
          </p>
          <div>
            <FieldLabel hint="Optional">Message</FieldLabel>
            <textarea className={`${inputClass} h-24 py-2 resize-none`} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} placeholder="e.g. We need regular night cover from next month." />
          </div>
        </div>
      )}
    </Modal>
  );
};

const ArchiveModal = ({ connection, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [blockers, setBlockers] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => { setReason(''); setBlockers([]); setError(''); }, [connection]);
  if (!connection) return null;

  const submit = async () => {
    if (!reason.trim()) { setError('Enter a reason.'); return; }
    setBusy(true); setError(''); setBlockers([]);
    try {
      const res = await homeAgencyService.archiveConnection(connection.id, reason.trim());
      toast.success(res.message);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBlockers(err?.response?.data?.blockers || []);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title={`Archive ${connection.agency?.name}`}
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Back</Button>
          <Button variant="danger" onClick={submit} loading={busy}>Archive connection</Button>
        </div>
      )}
    >
      <p className="text-[13.5px] text-[#475569] dark:text-gray-300 mb-3">
        You stop booking from this agency. Its outstanding offers are withdrawn; past bookings and hours stay in your
        records. You can restore the connection later from History.
      </p>
      <FieldLabel required>Reason</FieldLabel>
      <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />
      {error && <p className="mt-3 text-[12.5px] text-rose-600">{error}</p>}
      {blockers.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-[12.5px] text-rose-600">
          {blockers.map((b) => <li key={b}>{b}</li>)}
        </ul>
      )}
    </Modal>
  );
};

const ConnectionsTab = ({ rows, loading, reload, onViewStaff }) => {
  const [filter, setFilter] = useState('connected');
  const [finding, setFinding] = useState(false);
  const [archiving, setArchiving] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const groups = useMemo(() => ({
    connected: rows.filter((r) => r.status === 'connected'),
    pending: rows.filter((r) => r.status === 'pending'),
    history: rows.filter((r) => ['declined', 'cancelled', 'archived'].includes(r.status)),
  }), [rows]);

  const act = async (id, fn) => {
    setBusyId(id);
    try {
      const res = await fn();
      toast.success(res.message);
      reload();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const list = groups[filter] || [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterTabs
          value={filter}
          onChange={setFilter}
          tabs={[
            { value: 'connected', label: 'Connected', count: groups.connected.length },
            { value: 'pending', label: 'Pending', count: groups.pending.length },
            { value: 'history', label: 'History', count: groups.history.length },
          ]}
        />
        <Button leftIcon={<Search className="w-4 h-4" />} onClick={() => setFinding(true)}>Find agency</Button>
      </div>

      {!loading && !list.length && (
        <EmptyCard
          icon={Briefcase}
          title={filter === 'connected' ? 'No agencies connected yet' : filter === 'pending' ? 'No requests waiting' : 'No history yet'}
          text={filter === 'connected' ? 'Find an agency by name and send it a request. Once it accepts, you can approve its staff and book cover.' : undefined}
          action={filter === 'connected' ? <Button leftIcon={<Search className="w-4 h-4" />} onClick={() => setFinding(true)}>Find agency</Button> : null}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((c) => {
          const meta = CONNECTION_STATUS[c.status] || { label: c.status, tone: 'mute' };
          return (
            <div key={c.id} className={`${CARD} p-5 flex flex-col gap-3`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{c.agency?.name}</p>
                  <p className="text-[12px] text-[#94A3B8]">{c.agency?.org_id}</p>
                </div>
                <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
              </div>
              <div className="flex flex-col gap-1 text-[12.5px] text-[#64748B] dark:text-gray-400">
                {c.agency?.address && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{c.agency.address}</span>}
                {c.agency?.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{c.agency.phone}</span>}
                {c.agency?.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{c.agency.email}</span>}
              </div>
              {c.status === 'connected' && (
                <p className="text-[13px] text-[#334155] dark:text-gray-300">
                  <strong>{c.staff_approved}</strong> of {c.staff_total} staff approved for your home
                </p>
              )}
              {c.reason && c.status !== 'connected' && <p className="text-[12.5px] text-[#64748B]">Reason: {c.reason}</p>}
              {c.agency?.is_active === false && <p className="text-[12.5px] text-rose-600">This agency is suspended on ServPatch.</p>}
              <p className="text-[12px] text-[#94A3B8]">
                {c.status === 'pending' ? `Requested ${stampOf(c.requested_at)}${c.requested_by ? ` by ${c.requested_by}` : ''}` : `Updated ${stampOf(c.updated_at)}`}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                {c.status === 'connected' && (
                  <>
                    <Button size="sm" leftIcon={<Users className="w-4 h-4" />} onClick={() => onViewStaff(c.agency?.id)}>View staff</Button>
                    <Button size="sm" variant="secondary" leftIcon={<Archive className="w-4 h-4" />} onClick={() => setArchiving(c)}>Archive</Button>
                  </>
                )}
                {c.status === 'pending' && (
                  <Button size="sm" variant="secondary" leftIcon={<X className="w-4 h-4" />} loading={busyId === c.id} onClick={() => act(c.id, () => homeAgencyService.cancelConnection(c.id))}>Cancel request</Button>
                )}
                {c.status === 'archived' && (
                  <Button size="sm" variant="secondary" leftIcon={<RotateCcw className="w-4 h-4" />} loading={busyId === c.id} onClick={() => act(c.id, () => homeAgencyService.restoreConnection(c.id))}>Restore</Button>
                )}
                {['declined', 'cancelled'].includes(c.status) && c.agency?.is_active !== false && (
                  <Button size="sm" variant="secondary" leftIcon={<Send className="w-4 h-4" />} loading={busyId === c.id} onClick={() => act(c.id, () => homeAgencyService.requestConnection(c.agency.id))}>Request again</Button>
                )}
              </div>
              {c.history?.length > 0 && (
                <details className="text-[12px] text-[#64748B] dark:text-gray-400">
                  <summary className="cursor-pointer select-none">History</summary>
                  <ul className="mt-1.5 flex flex-col gap-1">
                    {c.history.slice().reverse().map((h, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i}><span className="capitalize font-semibold">{h.action}</span>{h.by_name ? ` by ${h.by_name}` : ''} · {stampOf(h.at)}{h.reason ? ` — ${h.reason}` : ''}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          );
        })}
      </div>

      <FindAgency open={finding} onClose={() => setFinding(false)} onRequested={() => { setFinding(false); setFilter('pending'); reload(); }} />
      <ArchiveModal connection={archiving} onClose={() => setArchiving(null)} onDone={() => { setArchiving(null); setFilter('history'); reload(); }} />
    </div>
  );
};

export default ConnectionsTab;
